import { LearningObjectTypes } from '@hendrx/lms/constants';
import { useMemo } from 'react';

import { XApiVerbName } from '@hendrx/shared/xapi-events';
import { useLearningObjectCommonData } from 'shared/components/providers/LearningObjectCommonDataProvider';
import { useUser } from 'shared/queries/user';
import { useWorkMode } from 'shared/utils/useWorkMode';

import { useCurrentPageId } from './useCurrentPageId';
import { useCurrentPageIndex } from './useCurrentPageIndex';
import { usePlenaryActivityInformation } from './usePlenaryActivityInformation';
// eslint-disable-next-line boundaries/element-types
import { useGetAssignmentQuery } from '../../../lms/queries/getAssignment';
// eslint-disable-next-line boundaries/element-types
import { useAuthenticatePlenaryStudentQuery } from '../../../plenary/queries/authenticatePlenaryStudent';
import { useCourseContextData } from '../../components/providers/CourseContextProvider/CourseContextProvider';
import { usePlenaryContextId } from '../../components/providers/PlenaryLessonContextIdProvider';
import { usePlenaryLessonStatus } from '../../components/providers/PlenaryLessonStatusProvider';

type PlenaryState = XApiVerbName.ExperiencedPlayed | XApiVerbName.ExperiencedStopped | undefined;

export function useEventData() {
  // Two page identities, deliberately both reported: `pageIndex` is the 1-based ordinal (plus the
  // `summary`/`submit`/`h<n>` sentinels) that CET's analytics key on as `page_<pageId>`, while
  // `pageContentId` is the page's content-object id — the only one that identifies a page outside its
  // activity, and the one the MOE/720 statements build object.ids from.
  const pageIndex = useCurrentPageIndex();
  const pageContentId = useCurrentPageId();

  const { learningObjectDraftId, learningObjectSnapshotId, assignmentId, learningObjectType } =
    useLearningObjectCommonData();
  const { courseCategoryId, courseDraftId, courseEnvironmentId } = useCourseContextData();

  const { isPlayerAssignment } = useWorkMode();

  const { data: assignmentData } = useGetAssignmentQuery(
    { assignmentId: assignmentId as string },
    isPlayerAssignment && !!assignmentId
  );

  const { isActive, isReady } = usePlenaryLessonStatus();
  const plenaryState: PlenaryState = !isReady
    ? undefined
    : isActive
    ? XApiVerbName.ExperiencedPlayed
    : XApiVerbName.ExperiencedStopped;
  const plenaryContextId = usePlenaryContextId() ?? '';
  const { user } = useUser();
  const activityInformation = usePlenaryActivityInformation();
  // Only the plenary-lobby flow needs this authenticate, and `plenaryStudentId` is consumed solely for a
  // PlenaryLesson (see eventData below). Gating on the type — not just `!user` — stops an anonymous 720
  // free-content embed (also user-less) from firing a pointless lobby authenticate that 401s.
  const { data: plenaryStudent } = useAuthenticatePlenaryStudentQuery(
    { activityInformation },
    !user && learningObjectType === LearningObjectTypes.PlenaryLesson
  );
  const plenaryStudentId = plenaryStudent?.id ?? '';

  const eventData = useMemo(
    () => ({
      activityId: learningObjectDraftId ?? '',
      snapshotId: learningObjectSnapshotId ?? '',
      courseId: courseDraftId,
      courseCategoryId,
      courseEnvironmentId,
      assignmentId,
      assignmentType: assignmentData?.assignmentType,
      pageId: pageIndex ?? '',
      pageContentId: pageContentId ?? undefined,
      ...(learningObjectType === LearningObjectTypes.PlenaryLesson
        ? { plenaryState, plenaryContextId, plenaryStudentId }
        : {})
    }),
    [
      learningObjectDraftId,
      learningObjectSnapshotId,
      courseDraftId,
      courseCategoryId,
      courseEnvironmentId,
      assignmentId,
      assignmentData?.assignmentType,
      pageIndex,
      pageContentId,
      learningObjectType,
      plenaryState,
      plenaryContextId,
      plenaryStudentId
    ]
  );

  return eventData;
}
